import React from "react";
import type { ReactNode } from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAlert } from "../app/notificationToast";
import { NewsletterModel } from "../../api/newsletter/NewsletterModel";
import { NewsletterAdapter } from "../../api/newsletter/NewsletterAdapter";

interface RgpdProviderProps {
  children: ReactNode;
}

export interface RgpdContextType {
  unregisterNewsletter: (email: string, companyId: number) => Promise<void>;
}

export const RgpdContext = React.createContext<RgpdContextType>(null!);

export function RgpdProvider(props: RgpdProviderProps) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();

  const newsletterModel = React.useMemo(() => new NewsletterModel(appConfig), [appConfig]);
  const newsletterClient = React.useMemo(
    () => new NewsletterAdapter(newsletterModel),
    [newsletterModel],
  );

  const unregisterNewsletter = React.useCallback(
    async (email: string, companyId: number): Promise<void> => {
      try {
        await newsletterClient.unregisterNewsletter(email, companyId);
      } catch (err) {
        console.error("Erreur RGPD:", err);
        showAlert("Impossible d'envoyer la demande. Réessayez plus tard.", "danger");
        throw err;
      }
    },
    [newsletterClient, showAlert],
  );

  const value = React.useMemo<RgpdContextType>(
    () => ({ unregisterNewsletter }),
    [unregisterNewsletter],
  );

  return <RgpdContext.Provider value={value}>{props.children}</RgpdContext.Provider>;
}

export function useRgpd(): RgpdContextType {
  const ctx = React.useContext(RgpdContext);
  if (!ctx) {
    throw new Error("useRgpd must be used within an RgpdProvider");
  }
  return ctx;
}
